import { API_URL } from '@/constants/config';
import { encodeQuery } from '~/modules/common/utils/routingUtils';
import { getAuthHeader } from '~/modules/common/utils/cookieUtils';

const base = API_URL + '/v3.5';

/**
 * Conversation Service
 *
 * @param {object} http axios instance from nuxt context
 * @param {object} cookies cookies from nuxt context
 */
export const conversationService = (http, cookies) => ({
  /**
   * Get Conversation
   *
   * @param {string} [conversationId]
   */
  async getConversation(conversationId) {
    const { data } = await http.$get(`${base}/conversations/${conversationId}`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Get Conversation List
   *
   * @param {object} params object
   * @param {number} [params.page]
   * @param {number} [params.limit]
   */
  async getConversationList({ page, limit } = {}) {
    const queryParams = encodeQuery({ limit, page });
    const { data, pagination } = await http.$get(`${base}/conversations${queryParams}`, {
      headers: getAuthHeader(cookies),
    });
    return { data, pagination };
  },

  /**
   * Get Conversation Messages
   *
   * @param {object} params object
   * @param {string} params.id conversation id
   * @param {number} [params.page]
   * @param {number} [params.limit]
   */
  async getMessages({ id, page, limit = 15 } = {}) {
    const queryParams = encodeQuery({ limit, page });
    const { data, pagination } = await http.$get(`${base}/conversations/${id}/messages${queryParams}`, {
      headers: getAuthHeader(cookies),
    });
    return { data, pagination };
  },

  /**
   * Send a message
   *
   * @param {object} params object
   * @param {string} params.id conversation id
   * @param {striing} params.message message
   */
  async sendMessage({ id, message }) {
    const body = {
      message,
    };
    const { data } = await http.$post(`${base}/conversations/${id}`, body, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Create a conversation
   *
   * @param {string} recipient user id
   */
  async createConversation(recipient) {
    const body = {
      recipient,
    };
    const { data } = await http.$post(`${base}/conversations/`, body, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Create a conversation with message
   *
   * @param {string} recipient user id
   * @param {string} message message
   */
  async createConversationWithMessage(recipient, message) {
    const body = {
      recipient,
      message,
    };
    const { data } = await http.$post(`${base}/conversations/create`, body, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Find conversation
   *
   * @param {string} recipient user id
   */
  async findConversation(recipient) {
    const queryParams = encodeQuery({ recipient });
    const { data } = await http.$get(`${base}/conversations/find${queryParams}`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },

  /**
   * Delete a message
   *
   * @param {string} conversationId
   * @param {string} messageId
   */
  async deleteMessage(conversationId, messageId) {
    const { data, message, status } = await http.$delete(`${base}/conversations/${conversationId}/${messageId}`, {
      headers: getAuthHeader(cookies),
    });
    return { data, message, status };
  },

  /**
   * Delete a conversation
   *
   * @param {string} conversationId
   */
  async deleteConversation(conversationId) {
    const { data, message, status } = await http.$delete(`${base}/conversations/${conversationId}`, {
      headers: getAuthHeader(cookies),
    });
    return { data, message, status };
  },

  /**
   * Get conversation count
   *
   */
  async getConversationsCount() {
    const { data } = await http.$get(`${base}/conversations/count`, {
      headers: getAuthHeader(cookies),
    });
    return { data };
  },
});
